import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import InputBase from '~/components/core/Atomic/InputBase';
import Typography from '~/components/core/Atomic/Typography';
import PermissionDeniedTooltip from '~/components/core/Permissions/PermissionDeniedTooltip';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { formatSortOrder, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { useRootStyles } from '../../assets/rootStyles';
import { SEARCH_MIXPANEL_EVENTS } from '../../pocs/mixpanel';
import mixpanel from '../CmsMain/mixpanel';
import { useCms } from '../hooks/useCms';

import CardDialog from './../CardDialog';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, TooltipIcon } from './../core';
import { AdvancedSearchIcon } from './../icons';
import PaginatedClaimSearchResults from './PaginatedClaimSearchResults';

import colors from '../../assets/colors.module.scss';
import styles from './searchBar.module.scss';

const DEFAULT_SORT_BY_COLUMN = { id: 'date_of_loss', order: 'desc' };
const DEFAULT_CLAIMS_PER_PAGE = 15;

function BarSearchField() {
  const { classes } = useRootStyles();
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_CLAIMS_PER_PAGE);
  const [sortByColumn, setSortByColumn] = useState(DEFAULT_SORT_BY_COLUMN);
  const [searchResults, setSearchResults] = useState();
  const [totalCount, setTotalCount] = useState(0);
  const [isSearchInProgress, setIsSearchInProgress] = useState(false);
  const { userOrganization } = useCms();

  const userHasClaimPermissions = useHasPermission({ action: PERMISSION_ACTIONS.CLAIM, verb: PERMISSION_VERBS.READ });
  const overrideSearchStyle = { backgroundColor: 'rgba(0,0,0,0.04)' };

  const fetchResults = async (page, rowsPerPage, sortByColumn) => {
    setIsSearchInProgress(true);
    try {
      const res = await axios.get(`/api/v1/claims/organization/${userOrganization.id}/search_query`, {
        params: {
          search: searchValue,
          page: page + 1,
          rows_per_page: rowsPerPage,
          sort_by_column: formatSortOrder(sortByColumn),
        },
      });

      setSearchResults(res.data.claims);
      setTotalCount(res.data.count);
      setIsSearchInProgress(false);

      return res.data;
    } catch (error) {
      setIsSearchInProgress(false);
      reportAxiosError(error);
      return null;
    }
  };

  const handleSearch = async (e) => {
    e && e.preventDefault();

    mixpanel.track(SEARCH_MIXPANEL_EVENTS.SEARCH_BUTTON_CLICKED, {
      search_value: searchValue,
    });

    const result = await fetchResults(0, rowsPerPage, sortByColumn);

    if (result) {
      mixpanel.track(SEARCH_MIXPANEL_EVENTS.SEARCH_RESULTS_RETURNED, {
        search_value: searchValue,
        num_results_returned: result.count || 0,
      });
    }
  };

  const handleCloseSearchResults = () => {
    setSearchResults(undefined);
    setPage(0);
    setIsSearchInProgress(false);
  };

  const handleClaimLinkClick = (claim) => {
    setSearchResults(undefined);

    const indexOfResult = searchResults?.indexOf(claim);
    mixpanel.track(SEARCH_MIXPANEL_EVENTS.CLAIM_OPENED_FROM_SEARCH_RESULTS, {
      search_value: searchValue,
      num_results_returned: totalCount,
      claim_clicked: claim.claim_id_display,
      result_clicked_order: indexOfResult + 1 + page * rowsPerPage,
    });
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchResults(newPage, rowsPerPage, sortByColumn);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchResults(0, newRowsPerPage, sortByColumn);
  };

  const handleSortByColumn = (_, newSortByColumn) => {
    setSortByColumn(newSortByColumn);
    setPage(0);
    fetchResults(0, rowsPerPage, newSortByColumn);
  };

  return (
    <>
      <PermissionDeniedTooltip action={PERMISSION_ACTIONS.CLAIM} verb={PERMISSION_VERBS.READ}>
        <div className={classes.search} style={userHasClaimPermissions ? null : overrideSearchStyle}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <InputBase
            disabled={!userHasClaimPermissions}
            placeholder="Search…"
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            inputProps={{
              onKeyDown: (e) => {
                if (e.key === 'Enter' && !isSearchInProgress) {
                  handleSearch(e);
                }
              },
            }}
            endAdornment={
              <div className={styles.advancedSearchButtonContainer}>
                {isSearchInProgress && (
                  <InputAdornment position="end">
                    <CircularProgress style={{ color: colors.loading }} size={20} />
                  </InputAdornment>
                )}
                {!isSearchInProgress && (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleSearch}
                      size="small"
                      style={{
                        visibility: searchValue ? 'visible' : 'hidden',
                        border: '1px solid rgba(0,0,0,0.09)',
                        height: '22px',
                        marginRight: '4px',
                        backgroundColor: '#FFFFFF',
                        color: '#rgba(0,0,0,0.6)',
                      }}
                    >
                      Search
                    </Button>
                    <IconButton className={cn(styles.advancedSearchIconButton)} component={Link} to="/advanced_search">
                      <TooltipIcon title="Advanced Search">
                        <AdvancedSearchIcon size={18} iconColor="currentColor" className={styles.advancedSearchIcon} />
                      </TooltipIcon>
                    </IconButton>
                  </InputAdornment>
                )}
              </div>
            }
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      </PermissionDeniedTooltip>

      {searchResults ? (
        searchResults.length > 0 ? (
          <PaginatedClaimSearchResults
            claims={searchResults}
            count={totalCount}
            hideLastTouchedDate
            onCancel={handleCloseSearchResults}
            onClaimLinkClick={handleClaimLinkClick}
            paginationProps={{
              page,
              rowsPerPage,
              count: totalCount,
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
              rowsPerPageOptions: [5, 10, 15, 25, 50],
            }}
            onSortByColumn={handleSortByColumn}
            title="Search Results"
            preResultsTableComponent={
              totalCount > 0 && (
                <div className={classes.cardDivRow}>
                  <span className={classes.textFieldRow}>{`Found ${totalCount} matching claims`}</span>
                </div>
              )
            }
          />
        ) : (
          <CardDialog isDialog noCardTitle onClose={() => setSearchResults(undefined)}>
            <Typography>No Results Were Found</Typography>
          </CardDialog>
        )
      ) : null}
    </>
  );
}

export default BarSearchField;
